import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { retrieveLaunchParams } from '@telegram-apps/bridge';
import { Section, Cell, List, Title, IconButton, Skeleton, Checkbox } from '@telegram-apps/telegram-ui';

import { Page } from '@/components/Page';
import { VideoPlayer } from '@/components/VideoPlayer';
import { useVideoPlayer } from '@/hooks/useVideoPlayer';
import { useAppStore } from '@/stores/appStore';
import { useCoursesStore } from '@/stores/coursesStore';
import { useVideoPlayerStore } from '@/stores/videoPlayerStore';

import styles from './CourseContentsPage.module.css';

export function CourseContentsPage() {
  const lp = useMemo(() => retrieveLaunchParams(), []);
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { getCourse, ensureCourseContents, isCourseContentsLoading, error, toggleLectureCompletion } = useCoursesStore();
  const { removeEntitlement } = useAppStore();

  const infoIcon = useMemo(() => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="8" r="1" fill="currentColor"/>
    </svg>
  ), []);

  useEffect(() => {
    if (courseId) {
      void ensureCourseContents(courseId);
    }
  }, [courseId, ensureCourseContents]);

  const course = courseId ? getCourse(courseId) : undefined;
  const isCourseLoading = !course || isCourseContentsLoading(courseId!);

  const {
    currentPlaybackId,
    muxTokens,
    currentLecture,
    isVideoLoading,
    playerRef,
    shouldAutoplay,
    handleTimeUpdate,
    loadLecture,
  } = useVideoPlayer(course ?? null);

  // Get the current course from the store to ensure timing
  const { currentCourse } = useVideoPlayerStore();

  const tryLoadLecture = async (lectureId: string, sectionId: string): Promise<void> => {
    if (courseId) {
      try {
        await loadLecture(courseId, sectionId, lectureId);
      } catch (error) {
        if (error instanceof Error && error.message === 'missing_entitlement') {
          // TODO: show a modal with the text about expired entitlement
          removeEntitlement(courseId);
          void navigate(`/course/${courseId}/landing`);
        } else {
          throw error;
        }
      }
    }
  };

  // Load first lecture when course is ready
  useEffect(() => {
    if (course && currentCourse && course.id === currentCourse.id && !currentLecture?.id) {
      const lastSeenLectureId = course.progress?.lastSeenLectureId;
      if (lastSeenLectureId) {
        const section = course.sections?.find(s => s.lectures.some(l => l.id === lastSeenLectureId));
        if (section) {
          void tryLoadLecture(lastSeenLectureId, section.id);
        }
      } else {
        const section = course.sections?.[0];
        const lecture = section?.lectures?.[0];
        if (section && lecture) {
          void tryLoadLecture(lecture.id, section.id);
        }
      }
    }
  }, [course, currentCourse, currentLecture, loadLecture]);

  return (
    <Page back={true} onBack={() => void navigate('/courses')}>
      <div className={styles.container}>
        <div className={styles.videoContainer}>
          <VideoPlayer
            isLoading={isCourseLoading || isVideoLoading}
            playbackId={currentPlaybackId}
            tokens={muxTokens}
            onTimeUpdate={handleTimeUpdate}
            playerRef={playerRef}
            shouldAutoplay={shouldAutoplay}
            aspectRatio={currentLecture?.asset?.aspect_ratio?.replace(':', '/') ?? 'auto'}
            metadata={{
              video_id: `${courseId}//${currentLecture?.id}`,
              video_title: currentLecture?.title ?? error ?? 'N/A',
              viewer_user_id: String(lp.tgWebAppData?.user?.id ?? ''),
            }}
          />
        </div>

        <div className={styles.contentContainer}>
          <Cell after={
            !isCourseLoading && <IconButton
              size="s"
              mode="bezeled"
              onClick={() => void navigate(`/course/${courseId}/landing`)}
            >
              {infoIcon}
            </IconButton>
          }>
            <Skeleton visible={isCourseLoading}>
              <Title>{course?.title ?? 'Курс загружается...'}</Title>
            </Skeleton>
          </Cell>

          <List>
            {isCourseLoading && (
              <Section header={
                <Skeleton visible={true} style={{ margin: '16px 16px 8px', height: 16, width: '30%' }} />
              }>
                <Cell>
                  <Skeleton visible={true}>.........................................</Skeleton>
                </Cell>
                <Cell>
                  <Skeleton visible={true}>...........................</Skeleton>
                </Cell>
                <Cell>
                  <Skeleton visible={true}>.....................................</Skeleton>
                </Cell>
                <Cell>
                  <Skeleton visible={true}>................</Skeleton>
                </Cell>
                <Cell>
                  <Skeleton visible={true}>..............................</Skeleton>
                </Cell>
                <Cell>
                  <Skeleton visible={true}>.................................</Skeleton>
                </Cell>
                <Cell>
                  <Skeleton visible={true}>................</Skeleton>
                </Cell>
                <Cell>
                  <Skeleton visible={true}>...........................</Skeleton>
                </Cell>
              </Section>
            )}

            {!isCourseLoading && course?.sections?.map((section) => (
              <Section key={section.id} header={section.title}>
                {section.lectures.length > 0 ? (
                  section.lectures.map((lecture) => {
                    const isCurrentLecture = currentLecture?.id === lecture.id;
                    return (
                      <Cell
                        multiline
                        key={`${section.id}-${lecture.id}`}
                        className={`${styles.lectureCell} ${isCurrentLecture ? styles.lectureCellActive : ''}`}
                        onClick={() => {
                          if (!isCurrentLecture) void tryLoadLecture(lecture.id, section.id);
                        }}
                        after={
                          // Hacky way to prevent the clicks on the checkbox from propagating to the Cell level click handler
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <Checkbox
                              checked={course.progress?.doneLectures?.[lecture.id] ?? false}
                              onChange={() => {
                                void toggleLectureCompletion(course.id, lecture.id);
                              }}
                            />
                          </div>
                        }
                      >
                        <div className={styles.lectureContent}>
                          <span>{lecture.title}</span>
                        </div>
                      </Cell>
                    );
                  })
                ) : (
                  <Cell>No lectures available</Cell>
                )}
              </Section>
            ))}
          </List>
        </div>
      </div>
    </Page>
  );
}